import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Crosshair, CheckCircle2, XCircle, Shield } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, SectionTitle, Spinner } from "@/components/Kit";
import { GradeChip } from "@/components/WebKit";

const PROFILES = [
  { k: "quick", label: "Quick", hint: "HTTP + headers — availability check" },
  { k: "posture", label: "Posture", hint: "HTTP, DNS/TLS/headers, tech + CVE" },
  { k: "integrity", label: "Integrity", hint: "HTTP, defacement/SEO, cloaking, IOC" },
  { k: "full", label: "Full (passive)", hint: "All passive modules" },
  { k: "active", label: "Active (ZAP)", hint: "Full + authorised ZAP — delegated to worker" },
];
const MODULES = [
  { k: "http", label: "HTTP / Redirect" }, { k: "dns_tls", label: "DNS · TLS · Headers" },
  { k: "fingerprint_cve", label: "Tech & CVE" }, { k: "defacement", label: "Defacement / SEO" },
  { k: "cloaking", label: "Cloaking" }, { k: "ioc", label: "Outbound / IOC" },
];

export default function WebScanLauncher() {
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const [targets, setTargets] = useState([]);
  const [targetId, setTargetId] = useState(params.get("target_id") || "");
  const [profile, setProfile] = useState("posture");
  const [mods, setMods] = useState([]);
  const [includeInferred, setIncludeInferred] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef();
  const timeoutRef = useRef();

  useEffect(() => {
    if (orgId) http.get("/web/targets", { params: { org_id: orgId } }).then((r) => {
      setTargets(r.data); setTargetId((prev) => prev || r.data[0]?.id || "");
    });
  }, [orgId]);

  useEffect(() => {
    if (targetId && profile === "active")
      http.get(`/web/targets/${targetId}/preflight`, { params: { org_id: orgId } }).then((r) => setPreflight(r.data.checks)).catch(() => setPreflight(null));
    else setPreflight(null);
  }, [targetId, profile, orgId]);

  useEffect(() => () => { clearInterval(pollRef.current); clearTimeout(timeoutRef.current); }, []);

  const target = targets.find((t) => t.id === targetId);
  const preflightFailed = profile === "active" && preflight && preflight.some((c) => !c.passed);

  const start = async () => {
    setBusy(true); setScan(null);
    try {
      const { data } = await http.post("/web/scans", {
        org_id: orgId, target_id: targetId, scan_profile: profile,
        modules: mods.length ? mods : undefined, include_inferred: includeInferred,
      });
      poll(data.scan_id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail?.message || e.response?.data?.detail)); setBusy(false); }
  };

  const poll = (sid) => {
    clearInterval(pollRef.current); clearTimeout(timeoutRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await http.get(`/web/scans/${sid}`, { params: { org_id: orgId } });
        setScan(data);
        if (["complete", "failed", "cancellation_requested"].includes(data.status)) {
          clearInterval(pollRef.current); clearTimeout(timeoutRef.current); setBusy(false);
          if (data.status === "complete") toast.success("Web scan complete");
          else if (data.status === "failed") toast.error("Scan failed");
        }
      } catch { /* transient poll error — keep polling */ }
    }, 3000);
    timeoutRef.current = setTimeout(() => {
      clearInterval(pollRef.current); setBusy(false);
      toast.message("Still running in the background — check the target's scan history.");
    }, 5 * 60 * 1000);
  };

  const toggleMod = (k) => setMods((m) => m.includes(k) ? m.filter((x) => x !== k) : [...m, k]);

  return (
    <div className="p-6 max-w-[920px] mx-auto fade-up">
      <h1 className="font-display text-3xl font-bold text-[#A5300F]">New Web Scan</h1>
      <p className="text-sm text-[#8A8175] mt-1 mb-5">Scan a live target. Findings stay in the Web Exposure family, separate from AppSec.</p>

      <Card className="p-6 space-y-5">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Target</label>
          <select data-testid="scan-target-select" value={targetId} onChange={(e) => setTargetId(e.target.value)}
            className="w-full mt-1 bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]">
            {targets.map((t) => <option key={t.id} value={t.id}>{t.hostname}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Scan Profile</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {PROFILES.map((p) => (
              <button key={p.k} onClick={() => setProfile(p.k)} data-testid={`profile-${p.k}`}
                className={`text-left p-3 rounded-md border transition-colors ${profile === p.k ? "border-[#A5300F] bg-[#FFF5EB]" : "border-[#E7D9C6] hover:bg-[#FBF7F1]"}`}>
                <div className="text-sm font-semibold">{p.label}</div>
                <div className="text-[11px] text-[#8A8175] mt-0.5">{p.hint}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Module override (optional)</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {MODULES.map((m) => (
              <label key={m.k} className={`text-xs px-2.5 py-1.5 rounded border cursor-pointer ${mods.includes(m.k) ? "border-[#A5300F] bg-[#FFF5EB] text-[#A5300F]" : "border-[#E7D9C6]"}`}>
                <input type="checkbox" className="hidden" checked={mods.includes(m.k)} onChange={() => toggleMod(m.k)} data-testid={`mod-${m.k}`} />{m.label}
              </label>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" checked={includeInferred} onChange={(e) => setIncludeInferred(e.target.checked)} className="accent-[#A5300F]" data-testid="include-inferred" />
            Include version-inferred (Potential) findings in the posture score
          </label>
        </div>

        {profile === "active" && (
          <div className="border border-[#E7D9C6] rounded-md p-4 bg-[#FDFBF8]" data-testid="preflight-panel">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#5C554C] mb-2"><Shield className="w-4 h-4" /> Active-scan pre-flight</div>
            {!preflight ? <div className="text-xs text-[#8A8175]">Configure a scan policy on the target to enable active scanning.</div> :
              preflight.map((c) => (
                <div key={c.check} className="flex items-center gap-2 text-sm py-0.5">
                  {c.passed ? <CheckCircle2 className="w-4 h-4 text-[#2E7D32]" /> : <XCircle className="w-4 h-4 text-[#A5300F]" />}
                  <span className={c.passed ? "text-[#5C554C]" : "text-[#A5300F]"}>{c.check}</span>
                </div>
              ))}
          </div>
        )}

        <button onClick={start} disabled={busy || !targetId || preflightFailed} data-testid="start-web-scan-btn"
          className="w-full bg-[#A5300F] text-white font-semibold py-2.5 rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <><Spinner /> Scanning…</> : <><Crosshair className="w-4 h-4" /> Start Scan</>}
        </button>

        {scan && (
          <div className="border-t border-[#ECE6DD] pt-4" data-testid="web-scan-status">
            <div className="flex items-center gap-2 text-sm">
              {scan.status === "complete" ? <CheckCircle2 className="w-5 h-5 text-[#2E7D32]" />
                : scan.status === "failed" ? <XCircle className="w-5 h-5 text-[#A5300F]" /> : <Spinner />}
              <span className="font-semibold capitalize">{scan.status}</span>
              {scan.posture_grade && <GradeChip grade={scan.posture_grade} score={scan.posture_score} />}
            </div>
            {scan.status === "complete" && <>
              <div className="flex gap-3 mt-3">
                {["critical", "high", "medium", "low", "info"].map((s) => (
                  <div key={s} className="text-center"><div className="stat-numeral text-2xl">{scan.summary?.[s] ?? 0}</div><div className="text-[10px] uppercase text-[#8A8175]">{s}</div></div>
                ))}
              </div>
              {(scan.modules_skipped || []).length > 0 && (
                <div className="mt-3 text-xs text-[#8A8175]">
                  <span className="font-semibold">Skipped: </span>{scan.modules_skipped.map((m) => `${m.module} (${m.reason})`).join("; ")}
                </div>
              )}
              <button onClick={() => navigate(`/web/targets/${targetId}`)} className="mt-4 text-sm text-[#A5300F] font-semibold underline">View target →</button>
            </>}
            {scan.status === "failed" && <div className="text-xs text-[#A5300F] mt-2 font-mono">{scan.error_message}</div>}
          </div>
        )}
      </Card>
    </div>
  );
}
